// src/pages/CasinoPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {Link} from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { GameAPI, PriceAPI } from '../services/api';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import GameCard from '../components/casino/GameCard';
import GameSessionModal from '../components/casino/GameSessionModal';
import GameIframe from '../components/casino/GameIframe';
import { LoadingSpinner } from '../components/common/Loading';

const CasinoPage = () => {
  const { user, token, isAuthenticated } = useAuth();
  const { 
    walletData, 
    refreshWallet, 
    isLoading: walletLoading,
    btcPrice,
    formatUSDWithSymbol,
    getWalletSummary
  } = useWallet();
  
  const [games, setGames] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('all');
  const [displayCount, setDisplayCount] = useState(24);
  const [allGamesLoaded, setAllGamesLoaded] = useState(false);
  
  // Game session state
  const [selectedGame, setSelectedGame] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showGameIframe, setShowGameIframe] = useState(false);
  const [gameUrl, setGameUrl] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [betAmount, setBetAmount] = useState(1.00);

  const loadMoreRef = useRef(null);

  // Calculate USD balance from wallet
  const usdBalance = walletData?.balance && btcPrice 
    ? walletData.balance * btcPrice 
    : 0;

  // Check for active session on mount
  useEffect(() => {
    const savedSession = localStorage.getItem('active_game_session');
    if (savedSession) {
      try {
        const session = JSON.parse(savedSession);
        const elapsed = Date.now() - session.startTime;
        if (elapsed < 30 * 60 * 1000) {
          setSessionId(session.sessionId);
          setShowGameIframe(true);
        } else {
          localStorage.removeItem('active_game_session');
        }
      } catch (e) {
        localStorage.removeItem('active_game_session');
      }
    }
  }, []);

  // Fetch games and refresh wallet on mount
  useEffect(() => {
    fetchGames();
    
    if (isAuthenticated && refreshWallet) {
      refreshWallet();
    }
    
    const interval = setInterval(() => {
      if (isAuthenticated && refreshWallet) {
        refreshWallet();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const fetchGames = async () => {
    try {
        setLoading(true);
        setError(null);
        
        // This now works without authentication
        const response = await GameAPI.getGames();
        
        // Handle different response formats
        if (response.success) {
            // The response might have data directly or nested
            const gamesData = response.data?.games || response.data || [];
            const providersData = response.data?.providers || [];
            
            setGames(gamesData);
            setProviders(providersData);
            setAllGamesLoaded(gamesData.length <= displayCount);
        } else {
            // Check if it's an auth error
            if (response.status === 401) {
                setError('Please login to access personalized game features');
                // Try to load cached games
                const cachedGames = localStorage.getItem('cached_games');
                if (cachedGames) {
                    try {
                        const parsed = JSON.parse(cachedGames);
                        if (parsed.games && parsed.games.length > 0) {
                            setGames(parsed.games);
                            setProviders(parsed.providers || []);
                            // Don't show error if we have cached data
                            setError(null);
                        }
                    } catch (e) {
                        // console.error('Error loading cached games:', e);
                    }
                }
            } else {
                setError(response.error || 'Failed to load games');
            }
        }
    } catch (err) {
        // console.error('Error fetching games:', err);
        setError('Error loading games. Please try again.');
        
        // Try to load from cache on error
        const cachedGames = localStorage.getItem('cached_games');
        if (cachedGames) {
            try {
                const parsed = JSON.parse(cachedGames);
                if (parsed.games && parsed.games.length > 0) {
                    setGames(parsed.games);
                    setProviders(parsed.providers || []);
                    setError(null);
                }
            } catch (e) {
                // console.error('Error loading cached games:', e);
            }
        }
    } finally {
        setLoading(false);
    }
};

useEffect(() => {
  if (games.length > 0) {
      localStorage.setItem('cached_games', JSON.stringify({
          games,
          providers,
          timestamp: Date.now()
      }));
  }
}, [games, providers]);


  // Load more games when user scrolls
  useEffect(() => {
    if (!loading && games.length > 0) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !loadingMore && !allGamesLoaded) {
            loadMoreGames();
          }
        },
        { threshold: 0.1 }
      );

      if (loadMoreRef.current) {
        observer.observe(loadMoreRef.current);
      }

      return () => {
        if (loadMoreRef.current) {
          observer.unobserve(loadMoreRef.current);
        }
      };
    }
  }, [loading, games, loadingMore, allGamesLoaded]);

  const loadMoreGames = () => {
    if (!loadingMore && !allGamesLoaded) {
      setLoadingMore(true);
      setTimeout(() => {
        setDisplayCount(prev => prev + 24);
        if (displayCount + 24 >= games.length) {
          setAllGamesLoaded(true);
        }
        setLoadingMore(false);
      }, 500);
    }
  };

  // Filter games
  const filteredGames = games.filter(game => {
    const matchesSearch = game.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (game.provider && game.provider.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesProvider = selectedProvider === 'all' || game.provider === selectedProvider;
    return matchesSearch && matchesProvider;
  });

  // Get displayed games (paginated)
  const displayedGames = filteredGames.slice(0, displayCount);

  // Get unique providers for filter
  const uniqueProviders = ['all', ...new Set(games.map(game => game.provider))];

  const handlePlayGame = (game) => {
    if (!isAuthenticated) {
      alert('Please login to play casino games');
      window.location.href = '/login';
      return;
    }
    
    setSelectedGame(game);
    setBetAmount(1.00);
    setShowSessionModal(true);
  };

  const handleStartSession = async (amountUsd) => {
    if (!selectedGame || !isAuthenticated) return;
    
    try {
      setSessionLoading(true);
      
      const response = await GameAPI.openSession(
        selectedGame.id,
        amountUsd,
        'en'
      );
      
      if (response.success) {
        const { session_id, game_url, amount_usd, amount_btc } = response.data;
        
        setSessionId(session_id);
        setGameUrl(game_url);
        setShowSessionModal(false);
        setShowGameIframe(true);
        
        localStorage.setItem('active_game_session', JSON.stringify({
          sessionId: session_id,
          gameId: selectedGame.id,
          gameName: selectedGame.name,
          amountUsd: amount_usd,
          amountBtc: amount_btc,
          startTime: Date.now()
        }));
        
        if (refreshWallet) {
          await refreshWallet();
        }
      } else {
        alert(response.error || 'Failed to start game session');
      }
    } catch (err) {
      // // console.error('Error starting session:', err);
      alert('Error starting game. Please try again.');
    } finally {
      setSessionLoading(false);
    }
  };

  const handleCloseGame = async () => {
    if (sessionId) {
      try {
        await GameAPI.closeSession(sessionId);
        if (refreshWallet) {
          await refreshWallet();
        }
      } catch (err) {
        // // console.error('Error closing session:', err);
      }
    }
    
    setShowGameIframe(false);
    setGameUrl('');
    setSessionId(null);
    localStorage.removeItem('active_game_session');
  };

  const handleCancelSession = () => {
    setShowSessionModal(false);
    setSelectedGame(null);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-deep-black">
        <Header />
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner size="lg" color="blue" />
        </div>
        <Footer />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-deep-black">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="bg-red-900/20 border border-red-800 rounded-xl p-6 text-center">
            <p className="text-red-400 text-lg">{error}</p>
            <button 
              onClick={fetchGames}
              className="mt-4 bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep-black">
      <Header /><br /><br /><br />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">🎰 Casino Games</h1>
              <p className="text-gray-400">
                {isAuthenticated ? 'Play the best casino games with real BTC' : 'Browse our collection of casino games'}
              </p>
              <p className="text-xs text-gray-500 mt-1">{games.length} games available</p>
            </div>
            
            {isAuthenticated ? (
              <div className="mt-4 md:mt-0 bg-gradient-to-r  rounded-xl p-4 min-w-[200px]">
                
              </div>
            ) : (
              <div className="mt-4 md:mt-0 bg-gray-800 rounded-xl p-4">
                <p className="text-gray-400 text-sm">Login to play</p>
                <a href="/login" className="text-blue-400 hover:text-blue-300 font-semibold">
                  Login or Register →
                </a>
              </div>
            )}
            <div className="flex items-center gap-2 w-full sm:w-auto">
           <Link to="/deposit" className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold text-sm">
            💰 Add Funds
            </Link>
                      
          </div>
          </div>
          
        </div>

        {/* Search & Filter - IMPROVED RESPONSIVE */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6">
          {/* Search Bar */}
          <div className="flex-1 min-w-0">
            <input
              type="text"
              placeholder="Search games..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-gray-800 text-white px-4 py-2.5 rounded-lg border border-gray-700 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 transition-colors"
            />
          </div>
          
          {/* Provider Filters - Scrollable */}
          <div className="relative">
            <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent max-w-full lg:max-w-md xl:max-w-lg 2xl:max-w-xl">
              {uniqueProviders.slice(0, 20).map(provider => (
                <button
                  key={provider}
                  onClick={() => setSelectedProvider(provider)}
                  className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors text-sm flex-shrink-0 ${
                    selectedProvider === provider
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {provider === 'all' ? '🎮 All Games' : provider}
                </button>
              ))}
            </div>
            {/* Gradient fade on right for scroll hint */}
            <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-deep-black to-transparent pointer-events-none lg:hidden"></div>
          </div>
        </div>

        {/* Games Grid */}
        {displayedGames.length === 0 ? (
          <div className="text-center py-12 bg-gray-900 rounded-xl">
            <p className="text-gray-400 text-lg">No games found</p>
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="mt-2 text-purple-400 hover:text-purple-300 transition-colors"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 mb-8">
              {displayedGames.map(game => (
                <GameCard
                  key={game.id}
                  game={game}
                  onPlay={handlePlayGame}
                  isAuthenticated={isAuthenticated}
                />
              ))}
            </div>
            
            {/* Load More Trigger */}
            {!allGamesLoaded && filteredGames.length > displayCount && (
              <div ref={loadMoreRef} className="text-center py-8">
                {loadingMore ? (
                  <div className="flex items-center justify-center">
                    <LoadingSpinner size="md" color="blue" />
                    <span className="ml-3 text-gray-400">Loading more games...</span>
                  </div>
                ) : (
                  <button
                    onClick={loadMoreGames}
                    className="bg-gray-800 hover:bg-gray-700 text-white px-6 py-3 rounded-lg transition-colors"
                  >
                    Load More Games ({filteredGames.length - displayCount} remaining)
                  </button>
                )}
              </div>
            )}
            
            {/* All loaded message */}
            {allGamesLoaded && filteredGames.length > 0 && (
              <div className="text-center py-4 text-gray-500 text-sm">
                All {filteredGames.length} games loaded
              </div>
            )}
          </>
        )}

        {/* Responsible Gaming */}
        <div className="bg-gray-900 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">🎯 Responsible Gaming</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl mb-2">💰</div>
              <h4 className="font-bold text-white mb-2">Set Limits</h4>
              <p className="text-gray-300 text-sm">Set deposit, loss, and time limits to control your gaming.</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-2">🔒</div>
              <h4 className="font-bold text-white mb-2">Secure & Fair</h4>
              <p className="text-gray-300 text-sm">All games are tested and certified for fairness.</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-2">📱</div>
              <h4 className="font-bold text-white mb-2">24/7 Support</h4>
              <p className="text-gray-300 text-sm">Contact support anytime for help with responsible gaming.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <GameSessionModal
        isOpen={showSessionModal}
        game={selectedGame}
        betAmount={betAmount}
        setBetAmount={setBetAmount}
        usdBalance={usdBalance}
        onStart={handleStartSession}
        onCancel={handleCancelSession}
        loading={sessionLoading}
        btcPrice={btcPrice}
      />

      {showGameIframe && (
        <GameIframe
          url={gameUrl}
          onClose={handleCloseGame}
          gameName={selectedGame?.name}
          sessionId={sessionId}
        />
      )}

      <Footer />
    </div>
  );
};

export default CasinoPage;